/** Shared labels, colours and small pills for the Conversion plan
 *  (used by the Plan page and the Dashboard card). */
import type { ConversionState, Discovered, DocType, Priority } from "../api";

/** localStorage key for the site last picked on the Conversion plan page. */
export const PLAN_SITE_KEY = "sdoc_plan_site";

export function readPlanSite(): string | null {
  try { return localStorage.getItem(PLAN_SITE_KEY); } catch { return null; }
}
export function writePlanSite(id: string): void {
  try { localStorage.setItem(PLAN_SITE_KEY, id); } catch { /* ignore */ }
}

export const PRIORITY_META: Record<Priority, { label: string; short: string; cls: string; help: string }> = {
  now: {
    label: "Convert now", short: "Now",
    cls: "bg-rose-50 text-rose-800 ring-rose-200",
    help: "In use and needed to access services (forms, guides, schedules, maps, policies), posted after the compliance date, or a current-year record in use.",
  },
  next: {
    label: "Convert next", short: "Next",
    cls: "bg-amber-50 text-amber-800 ring-amber-200",
    help: "Other current documents that are still in use.",
  },
  later: {
    label: "Later", short: "Later",
    cls: "bg-sky-50 text-sky-800 ring-sky-200",
    help: "Older documents still linked or in use, or recent ones with no evidence of use.",
  },
  archive: {
    label: "Archive candidate", short: "Archive",
    cls: "bg-slate-100 text-slate-700 ring-slate-300",
    help: "Older and no evidence of use — consider moving to a clearly marked archive section.",
  },
  duplicate: {
    label: "Duplicate", short: "Duplicate",
    cls: "bg-violet-50 text-violet-700 ring-violet-200",
    help: "Identical file to another PDF on this site — converting the original covers it.",
  },
  superseded: {
    label: "Older version", short: "Older version",
    cls: "bg-violet-50 text-violet-700 ring-violet-200",
    help: "An edited re-upload of this document exists and this copy isn't linked anywhere — convert the newer version instead.",
  },
  never: {
    label: "Never", short: "Never",
    cls: "bg-slate-50 text-slate-500 ring-slate-200",
    help: "Private visitor uploads, files hosted by third parties, or not actually a PDF. Can't be converted.",
  },
  unscored: {
    label: "Not scored yet", short: "Not scored",
    cls: "bg-white text-slate-500 ring-slate-300",
    help: "PDF details haven't been collected yet (scan pending or failed).",
  },
};

export function PriorityPill({ priority, className = "" }: { priority: Priority; className?: string }) {
  const meta = PRIORITY_META[priority] ?? PRIORITY_META.unscored;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full ring-1 ring-inset whitespace-nowrap ${meta.cls} ${className}`}>
      {meta.label}
    </span>
  );
}

export const DOC_TYPE_LABEL: Record<DocType, string> = {
  form: "Form",
  guide: "Guide",
  schedule: "Schedule",
  map: "Map",
  policy: "Policy / rules",
  meeting: "Meeting record",
  finance: "Finance",
  notice: "Notice",
  job: "Job posting",
  report: "Report",
  newsletter: "Newsletter",
  other: "Other",
};

export const CONVERSION_STATE_LABEL: Record<ConversionState, string> = {
  converting: "Converting",
  live: "Live",
  review: "Review needed",
  failed: "Failed",
};

/** Effective priority with a fallback for API rows that predate the plan fields. */
export function effectivePriority(r: Discovered): Priority {
  return r.effective_priority ?? r.priority_override ?? r.priority ?? "unscored";
}

/** Converted, but the file was replaced since — visitors get the plain PDF until it is re-converted. */
export function isOutOfDate(r: Discovered): boolean {
  return !!r.content_changed_at;
}

/** Why a row can never be converted, or null when it can. */
export function notConvertibleReason(r: Discovered): string | null {
  if (r.is_private) return "Private visitor upload — can't be converted";
  if (effectivePriority(r) === "never") return "Priority is Never — clear the override or change it to convert";
  return null;
}

export function rowPages(r: Discovered): number | null {
  return r.page_count ?? r.estimated_pages ?? null;
}

export function formatUsd(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "$0.00";
  if (n < 0.01) return `<$0.01`;
  if (n < 100) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(0)}`;
}
